import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, Inbox, MessageSquareText, Pencil, Plus, Tag, Trash2, Users } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { ColorPicker } from '@/components/ui/ColorPicker'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { Toggle } from '@/components/ui/Toggle'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { normalizeHex, readableTextColor } from '@/lib/color'
import { dateBR } from '@/lib/format'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'
import {
  SupportSettingsService as Service,
  type SupportCanned, type SupportInbox, type SupportLabel, type SupportMetrics, type SupportTeam,
} from '@/services/modules/SupportSettingsService'

type Tab = 'teams' | 'labels' | 'canned' | 'inboxes' | 'metrics'

/**
 * Variáveis que o backend troca pelo dado real ao enviar a resposta
 * (SupportCannedResponse::render). Manter igual à lista de lá — variável
 * escrita à mão que não existe no backend chega ao cliente como texto cru.
 */
const CANNED_VARIABLES: Array<{ token: string; label: string; example: string }> = [
  { token: '{{nome}}',        label: 'Nome do contato', example: 'Maria Silva' },
  { token: '{{plano}}',       label: 'Plano',           example: 'Ampla Mais Bronze' },
  { token: '{{carteirinha}}', label: 'Carteirinha',     example: '0001234567' },
]

/** Cor de partida de uma etiqueta nova (laranja da marca). */
const DEFAULT_LABEL_COLOR = '#f5821f'

/**
 * Cadastros da Central de Atendimento.
 *
 * Sem esta tela a central funciona mas não pode ser configurada pelo painel:
 * não haveria como criar a primeira fila nem a primeira resposta rápida.
 */
export function SupportSettingsPage() {
  const [tab, setTab] = useState<Tab>('teams')
  // Mesmas chaves das rotas /support/{teams,labels,canned,inboxes}.
  // Canais não têm exclusão no backend.
  const { can } = useAuthority()
  const perm = {
    teams:   { create: can('support.teams.create'),   edit: can('support.teams.edit'),   delete: can('support.teams.delete') },
    labels:  { create: can('support.labels.create'),  edit: can('support.labels.edit'),  delete: can('support.labels.delete') },
    canned:  { create: can('support.canned.create'),  edit: can('support.canned.edit'),  delete: can('support.canned.delete') },
    inboxes: { create: can('support.inboxes.create'), edit: can('support.inboxes.edit'), delete: false },
    metrics: { create: false, edit: false, delete: false },
  } satisfies Record<Tab, { create: boolean; edit: boolean; delete: boolean }>

  const [teams, setTeams] = useState<SupportTeam[]>([])
  const [labels, setLabels] = useState<SupportLabel[]>([])
  const [canned, setCanned] = useState<SupportCanned[]>([])
  const [inboxes, setInboxes] = useState<SupportInbox[]>([])
  const [agents, setAgents] = useState<Array<{ value: string; label: string }>>([])
  const [loading, setLoading] = useState(true)

  const [metrics, setMetrics] = useState<SupportMetrics | null>(null)
  const [teamForm, setTeamForm] = useState<Partial<SupportTeam> | null>(null)
  const [keywordDraft, setKeywordDraft] = useState('')
  const [labelForm, setLabelForm] = useState<Partial<SupportLabel> | null>(null)
  const [cannedForm, setCannedForm] = useState<Partial<SupportCanned> | null>(null)
  const [inboxForm, setInboxForm] = useState<Partial<SupportInbox> | null>(null)
  const [removing, setRemoving] = useState<{ kind: Tab; id: string; name: string } | null>(null)
  const cannedContentRef = useRef<HTMLTextAreaElement>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [t, l, c, i] = await Promise.all([
        Service.teams().catch(() => []),
        Service.labels().catch(() => []),
        Service.canned().catch(() => []),
        Service.inboxes().catch(() => []),
      ])
      setTeams(t); setLabels(l); setCanned(c); setInboxes(i)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    if (tab === 'metrics') Service.metrics().then(setMetrics).catch(() => setMetrics(null))
  }, [tab])
  useEffect(() => {
    Service.agents()
      .then((rows) => setAgents(rows.map((u) => ({ value: u.id, label: u.name }))))
      .catch(() => setAgents([]))
  }, [])

  /**
   * Insere a variável onde o cursor está (ou sobre o trecho selecionado), em
   * vez de sempre no fim: o atendente costuma clicar no meio da frase.
   */
  function insertVariable(token: string) {
    const el = cannedContentRef.current
    const current = cannedForm?.content ?? ''

    if (!el) {
      setCannedForm((f) => ({ ...f, content: current + token }))
      return
    }

    const start = el.selectionStart ?? current.length
    const end = el.selectionEnd ?? start
    const next = current.slice(0, start) + token + current.slice(end)
    setCannedForm((f) => ({ ...f, content: next }))

    // O cursor volta para depois do que foi inserido — senão o próximo clique
    // joga a variável seguinte para o começo do texto.
    const caret = start + token.length
    window.requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(caret, caret)
    })
  }

  async function save(fn: () => Promise<unknown>, ok: string, close: () => void) {
    setBusy(true)
    try {
      await fn()
      toast.success({ title: ok, description: 'Cadastro atualizado.' })
      close(); await load()
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function confirmRemove() {
    if (!removing) return
    setBusy(true)
    try {
      if (removing.kind === 'teams') await Service.deleteTeam(removing.id)
      if (removing.kind === 'labels') await Service.deleteLabel(removing.id)
      if (removing.kind === 'canned') await Service.deleteCanned(removing.id)
      toast.success({ title: 'Removido', description: `"${removing.name}" saiu do cadastro.` })
      setRemoving(null); await load()
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  const teamCols: DataTableColumn<SupportTeam>[] = [
    { key: 'name', header: 'Equipe', alwaysVisible: true, render: (t) => (
      <div>
        <p className="font-medium text-gray-800">{t.name}</p>
        <p className="text-[11px] text-gray-500">{t.description || 'Sem descrição'}</p>
      </div>
    ) },
    { key: 'members', header: 'Atendentes', align: 'center', render: (t) => <Badge variant="gray">{t.members.length}</Badge> },
    { key: 'keywords', header: 'Direciona por', render: (t) => (
      t.keywords?.length
        ? <div className="flex flex-wrap gap-1">{t.keywords.map((k) => <Badge key={k} variant="gray">{k}</Badge>)}</div>
        : <span className="text-xs text-gray-400">—</span>
    ) },
    { key: 'auto', header: 'Distribuição automática', align: 'center',
      render: (t) => <Badge variant={t.autoAssign ? 'green' : 'gray'}>{t.autoAssign ? 'ligada' : 'desligada'}</Badge> },
  ]

  const labelCols: DataTableColumn<SupportLabel>[] = [
    { key: 'name', header: 'Etiqueta', alwaysVisible: true, render: (l) => <LabelChip label={l} /> },
    { key: 'status', header: 'Status', align: 'center', render: (l) => <span className="text-xs text-gray-600">{l.status}</span> },
  ]

  const cannedCols: DataTableColumn<SupportCanned>[] = [
    { key: 'shortcut', header: 'Atalho', alwaysVisible: true, render: (c) => <code className="text-xs">{c.shortcut}</code> },
    { key: 'title', header: 'Título', render: (c) => <span className="text-gray-800">{c.title}</span> },
    { key: 'teams', header: 'Equipes', render: (c) => (
      (c.teams?.length ?? 0) === 0
        ? <Badge variant="blue">todas as equipes</Badge>
        : <div className="flex flex-wrap gap-1">{c.teams.map((t) => <Badge key={t.id} variant="gray">{t.name}</Badge>)}</div>
    ) },
  ]

  const inboxCols: DataTableColumn<SupportInbox>[] = [
    { key: 'name', header: 'Caixa', alwaysVisible: true, render: (i) => (
      <div>
        <p className="font-medium text-gray-800">{i.name}</p>
        <p className="text-[11px] text-gray-500">{i.channel === 'WHATSAPP' ? `Nº ${i.phoneNumberId ?? 'não configurado'}` : 'Aplicativo do assinante'}</p>
      </div>
    ) },
    { key: 'channel', header: 'Canal', align: 'center',
      render: (i) => <Badge variant={i.channel === 'WHATSAPP' ? 'green' : 'blue'}>{i.channel === 'WHATSAPP' ? 'WhatsApp' : 'App'}</Badge> },
    { key: 'team', header: 'Fila padrão', align: 'center', render: (i) => <span className="text-xs text-gray-600">{i.defaultTeam?.name ?? '—'}</span> },
  ]

  // ---------------------------------------------------------------- Exportação
  // Listas já vêm inteiras do backend — exporta o que está em memória.
  // Canais: só nome/canal/fila; nada de número, tokens ou credenciais.
  function exportRows<T>(filename: string, rows: T[], columns: ExportColumn<T>[]) {
    if (!rows.length) { toast.error('Nenhum registro para exportar.'); return }
    exportToXlsx(filename, rows, columns)
  }

  const teamExportCols: ExportColumn<SupportTeam>[] = [
    { header: 'Equipe',                  accessor: (t) => t.name },
    { header: 'Descrição',               accessor: (t) => t.description ?? '' },
    { header: 'Atendentes',              accessor: (t) => t.members.length },
    { header: 'Direciona por',           accessor: (t) => (t.keywords ?? []).join(', ') },
    { header: 'Distribuição automática', accessor: (t) => (t.autoAssign ? 'ligada' : 'desligada') },
  ]

  const labelExportCols: ExportColumn<SupportLabel>[] = [
    { header: 'Etiqueta', accessor: (l) => l.name },
    { header: 'Cor',      accessor: (l) => l.color },
    { header: 'Status',   accessor: (l) => l.status },
  ]

  const cannedExportCols: ExportColumn<SupportCanned>[] = [
    { header: 'Atalho',   accessor: (c) => c.shortcut },
    { header: 'Título',   accessor: (c) => c.title },
    { header: 'Equipes',  accessor: (c) => ((c.teams?.length ?? 0) === 0 ? 'todas as equipes' : c.teams.map((t) => t.name).join(', ')) },
    { header: 'Conteúdo', accessor: (c) => (c.content ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() },
  ]

  const inboxExportCols: ExportColumn<SupportInbox>[] = [
    { header: 'Caixa',       accessor: (i) => i.name },
    { header: 'Canal',       accessor: (i) => (i.channel === 'WHATSAPP' ? 'WhatsApp' : 'App') },
    { header: 'Fila padrão', accessor: (i) => i.defaultTeam?.name ?? '' },
  ]

  const sections: FormSection[] = [
    {
      key: 'teams', label: 'Equipes', icon: Users,
      content: (
        <SectionShell title="Equipes" description="Filas de atendimento. Cada chamado entra numa fila e é distribuído entre os atendentes dela.">
          <DataTable
            tableId="support.teams" columns={teamCols} rows={teams} loading={loading} getRowKey={(t) => t.id}
            emptyMessage="Nenhuma equipe cadastrada. Crie a primeira para começar a distribuir os chamados."
            exportable onExportAll={() => exportRows('atendimento-equipes', teams, teamExportCols)}
            rowActions={(t) => [
              ...(perm.teams.edit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => setTeamForm(t) }] : []),
              ...(perm.teams.delete ? [{ key: 'del', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, divider: perm.teams.edit,
                onClick: () => setRemoving({ kind: 'teams', id: t.id, name: t.name }) }] : []),
            ]}
          />
        </SectionShell>
      ),
    },
    {
      key: 'labels', label: 'Etiquetas', icon: Tag,
      content: (
        <SectionShell title="Etiquetas" description="Marcadores livres para classificar os chamados e filtrar depois.">
          <DataTable
            tableId="support.labels" columns={labelCols} rows={labels} loading={loading} getRowKey={(l) => l.id}
            emptyMessage="Nenhuma etiqueta cadastrada."
            exportable onExportAll={() => exportRows('atendimento-etiquetas', labels, labelExportCols)}
            rowActions={(l) => [
              ...(perm.labels.edit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => setLabelForm(l) }] : []),
              ...(perm.labels.delete ? [{ key: 'del', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, divider: perm.labels.edit,
                onClick: () => setRemoving({ kind: 'labels', id: l.id, name: l.name }) }] : []),
            ]}
          />
        </SectionShell>
      ),
    },
    {
      key: 'canned', label: 'Respostas rápidas', icon: MessageSquareText,
      content: (
        <SectionShell title="Respostas rápidas" description="Textos prontos que o atendente insere digitando o atalho na conversa.">
          <DataTable
            tableId="support.canned" columns={cannedCols} rows={canned} loading={loading} getRowKey={(c) => c.id}
            emptyMessage="Nenhuma resposta rápida cadastrada."
            exportable onExportAll={() => exportRows('atendimento-respostas-rapidas', canned, cannedExportCols)}
            rowActions={(c) => [
              ...(perm.canned.edit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />,
                onClick: () => setCannedForm(c) }] : []),
              ...(perm.canned.delete ? [{ key: 'del', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, divider: perm.canned.edit,
                onClick: () => setRemoving({ kind: 'canned', id: c.id, name: c.shortcut }) }] : []),
            ]}
          />
        </SectionShell>
      ),
    },
    {
      key: 'inboxes', label: 'Canais', icon: Inbox,
      content: (
        <SectionShell title="Canais" description="Por onde os chamados chegam. O canal de WhatsApp (número e credenciais) é criado e mantido em Configurações → Atendimento → WhatsApp; aqui você define o nome e a fila padrão de cada canal.">
          <DataTable
            tableId="support.inboxes" columns={inboxCols} rows={inboxes} loading={loading} getRowKey={(i) => i.id}
            emptyMessage="Nenhum canal configurado."
            exportable onExportAll={() => exportRows('atendimento-canais', inboxes, inboxExportCols)}
            rowActions={(i) => perm.inboxes.edit
              ? [{ key: 'edit', label: 'Configurar', icon: <Pencil size={14} />, onClick: () => setInboxForm(i) }]
              : []}
          />
        </SectionShell>
      ),
    },
    {
      key: 'metrics', label: 'Indicadores', icon: BarChart3,
      content: (
        <SectionShell title="Indicadores" description="Volume e tempos de atendimento no período.">
          {!metrics && <p className="text-sm text-gray-500">Carregando indicadores…</p>}
          {metrics && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                Período: {dateBR(metrics.period.from)} a {dateBR(metrics.period.to)}
              </p>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                <Kpi label="Chamados abertos" value={String(metrics.opened)} />
                <Kpi label="Encerrados" value={String(metrics.resolved)} />
                <Kpi label="Aguardando atendente" value={String(metrics.waiting)} tone={metrics.waiting > 0 ? 'warn' : undefined} />
                <Kpi label="1ª resposta (média)"
                  value={metrics.avgFirstResponseMinutes !== null ? `${metrics.avgFirstResponseMinutes} min` : '—'} />
                <Kpi label="Resolução (média)"
                  value={metrics.avgResolutionHours !== null ? `${metrics.avgResolutionHours} h` : '—'} />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <BarList title="Por canal" rows={metrics.byChannel.map((c) => ({
                  label: c.channel === 'WHATSAPP' ? 'WhatsApp' : 'Aplicativo', value: c.total,
                }))} />
                <BarList title="Por atendente" rows={metrics.byAgent.map((a) => ({ label: a.agent, value: a.total }))} />
              </div>
            </div>
          )}
        </SectionShell>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Configurações do atendimento"
        subtitle="Equipes, etiquetas, respostas rápidas e canais"
        crumbs={[{ label: 'Atendimento' }, { label: 'Configurações' }]}
        actions={
          perm[tab].create ? (
            <Button iconLeft={<Plus size={14} />} onClick={() => {
              if (tab === 'teams') setTeamForm({ autoAssign: false, members: [], keywords: [] })
              if (tab === 'labels') setLabelForm({ color: DEFAULT_LABEL_COLOR })
              if (tab === 'canned') setCannedForm({})
              if (tab === 'inboxes') setInboxForm({ channel: 'APP', name: '' })
            }}>
              {tab === 'teams' ? 'Nova equipe'
                : tab === 'labels' ? 'Nova etiqueta'
                : tab === 'canned' ? 'Nova resposta' : 'Novo canal'}
            </Button>
          ) : undefined
        }
      />

      <FormSidemenu sections={sections} activeKey={tab} onChange={(k) => setTab(k as Tab)} />

      {/* ------------------------------------------------ Equipe */}
      <Modal
        open={!!teamForm} onClose={() => setTeamForm(null)} size="sm"
        title={teamForm?.id ? 'Editar equipe' : 'Nova equipe'}
        footer={<>
          <Button variant="ghost" onClick={() => setTeamForm(null)} disabled={busy}>Cancelar</Button>
          <Button loading={busy} disabled={!teamForm?.name?.trim()}
            onClick={() => void save(() => Service.saveTeam({
              id: teamForm?.id, name: teamForm!.name!, description: teamForm?.description ?? undefined,
              autoAssign: !!teamForm?.autoAssign,
              keywords: teamForm?.keywords ?? [],
              memberIds: (teamForm?.members ?? []).map((m) => m.id),
            }), 'Equipe salva', () => setTeamForm(null))}
          >Salvar</Button>
        </>}
      >
        <div className="space-y-3">
          <Input label="Nome" required value={teamForm?.name ?? ''} placeholder="Ex.: Financeiro"
            onChange={(e) => setTeamForm((f) => ({ ...f, name: e.target.value }))} />
          <Input label="Descrição" value={teamForm?.description ?? ''} placeholder="O que esta fila atende"
            onChange={(e) => setTeamForm((f) => ({ ...f, description: e.target.value }))} />
          <Select
            label="Atendentes" value=""
            onChange={(e) => {
              const a = agents.find((x) => x.value === e.target.value)
              if (!a) return
              setTeamForm((f) => ({
                ...f,
                members: [...(f?.members ?? []).filter((m) => m.id !== a.value), { id: a.value, name: a.label }],
              }))
            }}
            options={agents} placeholder="Adicionar atendente" searchPlaceholder="Buscar atendente"
          />
          {(teamForm?.members ?? []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {teamForm!.members!.map((m) => (
                <button key={m.id} type="button" className="cursor-pointer rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-200"
                  onClick={() => setTeamForm((f) => ({ ...f, members: (f?.members ?? []).filter((x) => x.id !== m.id) }))}>
                  {m.name} ✕
                </button>
              ))}
            </div>
          )}
          <Input
            label="Palavras que direcionam para esta fila"
            value={keywordDraft}
            placeholder="Digite e tecle Enter (ex.: boleto)"
            hint="A primeira mensagem do cliente é comparada com estas palavras, sem acento e sem caixa."
            onChange={(e) => setKeywordDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !keywordDraft.trim()) return
              e.preventDefault()
              const nova = keywordDraft.trim()
              setTeamForm((f) => ({ ...f, keywords: [...new Set([...(f?.keywords ?? []), nova])] }))
              setKeywordDraft('')
            }}
          />
          {(teamForm?.keywords ?? []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {teamForm!.keywords!.map((k) => (
                <button key={k} type="button" className="cursor-pointer rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-200"
                  onClick={() => setTeamForm((f) => ({ ...f, keywords: (f?.keywords ?? []).filter((x) => x !== k) }))}>
                  {k} ✕
                </button>
              ))}
            </div>
          )}
          <Toggle
            checked={!!teamForm?.autoAssign}
            onChange={(v) => setTeamForm((f) => ({ ...f, autoAssign: v }))}
            label="Distribuir chamados automaticamente entre os atendentes"
          />
        </div>
      </Modal>

      {/* ------------------------------------------------ Etiqueta */}
      <Modal
        open={!!labelForm} onClose={() => setLabelForm(null)} size="sm"
        title={labelForm?.id ? 'Editar etiqueta' : 'Nova etiqueta'}
        footer={<>
          <Button variant="ghost" onClick={() => setLabelForm(null)} disabled={busy}>Cancelar</Button>
          <Button loading={busy} disabled={!labelForm?.name?.trim()}
            onClick={() => void save(() => Service.saveLabel({
              id: labelForm?.id, name: labelForm!.name!,
              color: normalizeHex(labelForm?.color) ?? DEFAULT_LABEL_COLOR,
            }), 'Etiqueta salva', () => setLabelForm(null))}
          >Salvar</Button>
        </>}
      >
        <div className="space-y-3">
          <Input label="Nome" required value={labelForm?.name ?? ''} placeholder="Ex.: Segunda via de boleto"
            onChange={(e) => setLabelForm((f) => ({ ...f, name: e.target.value }))} />
          <ColorPicker
            label="Cor"
            value={labelForm?.color ?? DEFAULT_LABEL_COLOR}
            previewText={labelForm?.name ?? ''}
            hint="Escolha uma cor da paleta ou clique no hexadecimal para abrir o seletor."
            onChange={(hex) => setLabelForm((f) => ({ ...f, color: hex }))}
          />
        </div>
      </Modal>

      {/* ------------------------------------------------ Resposta rápida */}
      <Modal
        open={!!cannedForm} onClose={() => setCannedForm(null)} size="md"
        title={cannedForm?.id ? 'Editar resposta rápida' : 'Nova resposta rápida'}
        footer={<>
          <Button variant="ghost" onClick={() => setCannedForm(null)} disabled={busy}>Cancelar</Button>
          <Button loading={busy}
            disabled={!cannedForm?.shortcut?.trim() || !cannedForm?.title?.trim() || !cannedForm?.content?.trim()}
            onClick={() => void save(() => Service.saveCanned({
              id: cannedForm?.id, shortcut: cannedForm!.shortcut!, title: cannedForm!.title!,
              content: cannedForm!.content!, teamIds: cannedForm?.teamIds ?? [],
            }), 'Resposta salva', () => setCannedForm(null))}
          >Salvar</Button>
        </>}
      >
        <div className="space-y-3">
          <Input label="Atalho" required value={cannedForm?.shortcut ?? ''} placeholder="/boleto"
            hint="É o que o atendente digita no compositor."
            onChange={(e) => setCannedForm((f) => ({ ...f, shortcut: e.target.value }))} />
          <Input label="Título" required value={cannedForm?.title ?? ''} placeholder="Segunda via de boleto"
            onChange={(e) => setCannedForm((f) => ({ ...f, title: e.target.value }))} />
          <Textarea ref={cannedContentRef} label="Texto" required rows={5} value={cannedForm?.content ?? ''}
            spellCheck lang="pt-BR" autoCorrect="on"
            placeholder="Olá {{nome}}, segue a segunda via do seu boleto."
            onChange={(e) => setCannedForm((f) => ({ ...f, content: e.target.value }))} />

          <div>
            <p className="mb-1.5 text-[11px] text-gray-500">
              Clique para inserir onde o cursor estiver — ou digite direto no texto:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {CANNED_VARIABLES.map((v) => (
                <button
                  key={v.token}
                  type="button"
                  onClick={() => insertVariable(v.token)}
                  title={`${v.label} — ex.: ${v.example}`}
                  className="cursor-pointer inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-700 ring-1 ring-gray-200 transition hover:bg-brand-50 hover:text-brand-700 hover:ring-brand-200"
                >
                  <Plus size={11} />
                  <code>{v.token}</code>
                  <span className="text-gray-500">{v.label}</span>
                </button>
              ))}
            </div>
          </div>
          <Select
            label="Equipes" value=""
            options={teams
              .filter((t) => !(cannedForm?.teamIds ?? []).includes(t.id))
              .map((t) => ({ value: t.id, label: t.name }))}
            placeholder="Todas as equipes"
            searchPlaceholder="Buscar equipe"
            hint="Sem nenhuma equipe marcada, a resposta aparece para a central inteira."
            onChange={(e) => {
              const t = teams.find((x) => x.id === e.target.value)
              if (!t) return
              setCannedForm((f) => ({
                ...f,
                teams: [...(f?.teams ?? []).filter((x) => x.id !== t.id), { id: t.id, name: t.name }],
                teamIds: [...(f?.teamIds ?? []).filter((x) => x !== t.id), t.id],
              }))
            }}
          />
          {(cannedForm?.teamIds ?? []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {(cannedForm?.teams ?? []).map((t) => (
                <button key={t.id} type="button"
                  className="cursor-pointer rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-200"
                  onClick={() => setCannedForm((f) => ({
                    ...f,
                    teams: (f?.teams ?? []).filter((x) => x.id !== t.id),
                    teamIds: (f?.teamIds ?? []).filter((x) => x !== t.id),
                  }))}>
                  {t.name} ✕
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* ------------------------------------------------ Caixa */}
      <Modal
        open={!!inboxForm} onClose={() => setInboxForm(null)} size="sm"
        title={inboxForm?.id ? `Configurar ${inboxForm?.name ?? ''}` : 'Novo canal'}
        footer={<>
          <Button variant="ghost" onClick={() => setInboxForm(null)} disabled={busy}>Cancelar</Button>
          <Button loading={busy} disabled={!inboxForm?.name?.trim()}
            onClick={() => void save(() => inboxForm!.id
              ? Service.saveInbox(inboxForm!.id!, {
                name: inboxForm!.name!,
                defaultTeamId: inboxForm?.defaultTeam?.id,
              })
              : Service.createInbox({
                channel: (inboxForm?.channel as 'APP' | 'WHATSAPP') ?? 'APP',
                name: inboxForm!.name!,
                defaultTeamId: inboxForm?.defaultTeam?.id,
              }), inboxForm?.id ? 'Canal atualizado' : 'Canal criado', () => setInboxForm(null))}
          >Salvar</Button>
        </>}
      >
        <div className="space-y-3">
          {!inboxForm?.id && (
            <Select
              label="Canal" value={inboxForm?.channel ?? 'APP'}
              options={[{ value: 'APP', label: 'Aplicativo do assinante' }, { value: 'WHATSAPP', label: 'WhatsApp' }]}
              clearable={false}
              onChange={(e) => setInboxForm((f) => ({ ...f, channel: e.target.value as 'APP' | 'WHATSAPP' }))}
            />
          )}
          <Input label="Nome" required value={inboxForm?.name ?? ''} placeholder="Nome exibido na fila"
            onChange={(e) => setInboxForm((f) => ({ ...f, name: e.target.value }))} />
          <Select
            label="Fila padrão" value={inboxForm?.defaultTeam?.id ?? ''}
            options={teams.map((t) => ({ value: t.id, label: t.name }))}
            placeholder="Sem fila padrão"
            hint="Chamados novos deste canal caem nesta equipe."
            onChange={(e) => {
              const t = teams.find((x) => x.id === e.target.value)
              setInboxForm((f) => ({ ...f, defaultTeam: t ? { id: t.id, name: t.name } : null }))
            }}
          />
          {inboxForm?.channel === 'WHATSAPP' && (
            <div className="rounded-md bg-gray-50 px-3 py-2.5 text-xs ring-1 ring-gray-200">
              <p className="mb-0.5 font-medium text-gray-700">Número na Meta</p>
              <p className="font-mono text-gray-600">{inboxForm?.phoneNumberId || 'não configurado'}</p>
              <p className="mt-1.5 text-gray-500">
                O número e as credenciais são definidos em{' '}
                <Link to="/settings/support/whatsapp" className="cursor-pointer text-brand-600 hover:underline">
                  Configurações → Atendimento → WhatsApp
                </Link>. Aqui você ajusta apenas o nome e a fila padrão.
              </p>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!removing} onClose={() => setRemoving(null)} onConfirm={() => void confirmRemove()}
        loading={busy} variant="danger" title="Remover do cadastro?"
        description={`"${removing?.name ?? ''}" será removido. Equipes com chamados em andamento não podem ser excluídas.`}
        confirmLabel="Remover"
      />
    </>
  )
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div className={`rounded-lg p-4 ring-1 ${tone === 'warn' ? 'bg-amber-50 ring-amber-200' : 'bg-white ring-gray-200'}`}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-800">{value}</p>
    </div>
  )
}

/**
 * Barras proporcionais — sem biblioteca de gráfico. Para cinco ou seis linhas,
 * uma barra em CSS diz o mesmo e não adiciona dependência ao bundle.
 */
function BarList({ title, rows }: { title: string; rows: Array<{ label: string; value: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.value))

  return (
    <div className="rounded-lg bg-white p-4 ring-1 ring-gray-200">
      <p className="mb-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">{title}</p>
      {rows.length === 0 && <p className="text-sm text-gray-500">Sem dados no período.</p>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-700">{r.label}</span>
              <span className="tabular-nums text-gray-500">{r.value}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-gray-100">
              <div className="h-1.5 rounded-full bg-brand-500" style={{ width: `${(r.value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SectionShell({ title, description, children }: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-md bg-white p-5 ring-1 ring-gray-200 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
      </div>
      {children}
    </div>
  )
}

/**
 * Etiqueta com a cor escolhida pelo usuário. O texto vira preto ou branco
 * conforme a luminância do fundo — cor livre sem isso produz rótulo ilegível.
 */
function LabelChip({ label }: { label: SupportLabel }) {
  const color = normalizeHex(label.color) ?? DEFAULT_LABEL_COLOR
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium"
      style={{ backgroundColor: color, color: readableTextColor(color) }}
    >
      {label.name}
    </span>
  )
}
